import { getPrintFileBatch, IPrintFileBatchDetails, PrintFileReviewResult } from '../../api/printFileBatches';

/**
 * GET /print-files/batches/{id} results, shared by "Load to view", the moderator's pack
 * statistics and the file history in the review modal. Cached briefly so opening a pack
 * right after looking at its stats costs no extra request.
 */

const DEFAULT_MAX_AGE_MS = 60 * 1000;
const cache = new Map<string, { at: number; promise: Promise<IPrintFileBatchDetails> }>();

export const fetchPackDetails = (batchId: string, maxAgeMs = DEFAULT_MAX_AGE_MS): Promise<IPrintFileBatchDetails> => {
    const hit = cache.get(batchId);
    if (hit && Date.now() - hit.at < maxAgeMs) return hit.promise;
    const promise = getPrintFileBatch(batchId);
    cache.set(batchId, { at: Date.now(), promise });
    promise.catch(() => {
        if (cache.get(batchId)?.promise === promise) cache.delete(batchId);
    });
    return promise;
};

/** Gaps between two reviews longer than this are breaks, not time spent on a file. */
const BREAK_MS = 20 * 60 * 1000;

const median = (values: number[]): number | null => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
};

const ts = (iso: string | null | undefined) => {
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(t) ? null : t;
};

export interface IPackStats {
    files: number;
    reviewed: number;
    pending: number;
    results: Record<PrintFileReviewResult, number>;
    /** Reviewed files whose final is a manually edited upload. */
    manualEdits: number;
    /** Reviewed files where the AI-upscaled draft was chosen as the final. */
    upscaledChosen: number;
    /** Files that have an AI-upscaled variant at all. */
    upscaledAvailable: number;
    /** Decided by the automatic path, not a designer. */
    autoReviewed: number;
    /** Files a designer reviewed more than once. */
    changedDecision: number;
    /** Who made the final decision, by number of files. */
    reviewers: { name: string; files: number }[];
    firstReviewAt: number | null;
    lastReviewAt: number | null;
    /** First → last designer review. */
    reviewSpanMs: number | null;
    /** Typical time between two consecutive designer reviews (breaks excluded). */
    medianPerFileMs: number | null;
    /** Typical wait from the pipeline finishing a file to its first review. */
    medianWaitMs: number | null;
    /** Pack created → last review, for done packs. */
    turnaroundMs: number | null;
    /** Files whose last pipeline run did not succeed. */
    pipelineIssues: number;
    /** Files processed by the pipeline more than once. */
    reprocessed: number;
}

export const computePackStats = (details: IPrintFileBatchDetails): IPackStats => {
    const results: Record<PrintFileReviewResult, number> = { ok: 0, need_design: 0, need_cs: 0 };
    const reviewers = new Map<string, number>();
    const designerReviewTimes: number[] = [];
    const waits: number[] = [];
    let reviewed = 0;
    let manualEdits = 0;
    let upscaledChosen = 0;
    let upscaledAvailable = 0;
    let autoReviewed = 0;
    let changedDecision = 0;
    let pipelineIssues = 0;
    let reprocessed = 0;

    details.printFiles.forEach((f) => {
        const { history } = f;
        const last = history.reviews[history.reviews.length - 1];
        const result = (f.reviewResult ?? last?.result ?? null) as PrintFileReviewResult | null;
        if (result && result in results) {
            results[result] += 1;
            reviewed += 1;
        }
        if (f.draftPrintReadyFileUpscaledUrl) upscaledAvailable += 1;
        // The server stores an approved upscaled draft as a "manual edit" pointing at it.
        const upscaled = !!result && !!f.draftPrintReadyFileUpscaledUrl && f.finalPrintReadyUrl === f.draftPrintReadyFileUpscaledUrl;
        if (upscaled) upscaledChosen += 1;
        else if (result && (f.hasManualEdits || last?.finalSource === 'manual_edits')) manualEdits += 1;
        if (last?.auto) autoReviewed += 1;
        const designerReviews = history.reviews.filter((r) => !r.auto);
        if (designerReviews.length > 1) changedDecision += 1;
        const decider = [...designerReviews].reverse().find((r) => r.by)?.by;
        if (decider) reviewers.set(decider, (reviewers.get(decider) ?? 0) + 1);
        designerReviews.forEach((r) => {
            const t = ts(r.at);
            if (t != null) designerReviewTimes.push(t);
        });
        const processed = ts(history.processedAt);
        const firstReview = ts(designerReviews[0]?.at);
        if (processed != null && firstReview != null && firstReview >= processed) waits.push(firstReview - processed);
        if (history.jobStatus && history.jobStatus !== 'succeeded') pipelineIssues += 1;
        if (history.jobRuns > 1) reprocessed += 1;
    });

    designerReviewTimes.sort((a, b) => a - b);
    const gaps = designerReviewTimes.slice(1).map((t, i) => t - designerReviewTimes[i]).filter((g) => g <= BREAK_MS);
    const firstReviewAt = designerReviewTimes[0] ?? null;
    const lastReviewAt = designerReviewTimes[designerReviewTimes.length - 1] ?? null;
    const created = ts(details.createdAt);

    return {
        files: details.printFiles.length,
        reviewed,
        pending: details.printFiles.length - reviewed,
        results,
        manualEdits,
        upscaledChosen,
        upscaledAvailable,
        autoReviewed,
        changedDecision,
        reviewers: Array.from(reviewers, ([name, files]) => ({ name, files })).sort((a, b) => b.files - a.files),
        firstReviewAt,
        lastReviewAt,
        reviewSpanMs: firstReviewAt != null && lastReviewAt != null ? lastReviewAt - firstReviewAt : null,
        medianPerFileMs: median(gaps),
        medianWaitMs: median(waits),
        turnaroundMs: details.status === 'done' && created != null && lastReviewAt != null ? lastReviewAt - created : null,
        pipelineIssues,
        reprocessed,
    };
};

/** 09/28/2026, 17:00:00 — full timestamp for history lines. */
export const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

/** 45s · 12m · 2h 05m · 3d 4h */
export const fmtDuration = (ms: number | null | undefined): string => {
    if (ms == null || ms < 0) return '—';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
};
